import { Component, OnInit } from '@angular/core';
import { environment } from 'src/environments/environments';
import { BaseTemplateService } from '../base-template/base-template.service';
import { AuthService } from '../services/auth.service';
import { PermissionsMap } from '../models/user-data.model';

interface ApiEndpoint {
    method: string;
    endpoint: string;
    description: string;
    authentication: boolean;
    parameters?: ApiParameter[];
    responses?: ApiResponse[];
    example?: any;
}

interface ApiParameter {
    name: string;
    type: string;
    required: boolean;
    description: string;
    example?: any;
}

interface ApiResponse {
    code: number;
    description: string;
    example?: any;
}

interface ApiSection {
    title: string;
    description: string;
    endpoints: ApiEndpoint[];
}

@Component({
    selector: 'app-api-docs',
    templateUrl: './api-docs.component.html',
    styleUrls: ['./api-docs.component.scss']
})
export class ApiDocsComponent implements OnInit {
    apiDocsUrl: string = '';
    apiBaseUrl: string = '';
    selectedTab: string = 'overview';
    searchTerm: string = '';
    showPdf: boolean = true;
    hasApiAccess = false;

    apiSections: ApiSection[] = [
        {
            title: 'Authentication',
            description: 'Endpoints for user authentication and authorization',
            endpoints: [
                {
                    method: 'POST',
                    endpoint: '/auth/login',
                    description: 'Authenticate user with credentials',
                    authentication: false,
                    parameters: [
                        { name: 'email', type: 'string', required: true, description: 'User email address' },
                        { name: 'password', type: 'string', required: true, description: 'User password' }
                    ],
                    responses: [
                        { code: 200, description: 'Successful authentication' },
                        { code: 401, description: 'Invalid credentials' }
                    ]
                }
            ]
        },
        {
            title: 'Search',
            description: 'Search functionality for songs, artists, and albums',
            endpoints: [
                {
                    method: 'GET',
                    endpoint: '/search',
                    description: 'Search across all content types',
                    authentication: true,
                    parameters: [
                        { name: 'q', type: 'string', required: true, description: 'Search query' },
                        { name: 'type', type: 'string', required: false, description: 'Content type filter (songs, artists, albums)' },
                        { name: 'limit', type: 'number', required: false, description: 'Number of results to return (default: 20)' }
                    ]
                }
            ]
        },
        {
            title: 'Songs',
            description: 'Manage song information and metadata',
            endpoints: [
                {
                    method: 'GET',
                    endpoint: '/songs',
                    description: 'Get list of songs',
                    authentication: true
                },
                {
                    method: 'POST',
                    endpoint: '/songs',
                    description: 'Create a new song',
                    authentication: true
                },
                {
                    method: 'GET',
                    endpoint: '/songs/{id}',
                    description: 'Get specific song details',
                    authentication: true
                }
            ]
        },
        {
            title: 'Artists',
            description: 'Artist management and information',
            endpoints: [
                {
                    method: 'GET',
                    endpoint: '/artists',
                    description: 'Get list of artists',
                    authentication: true
                },
                {
                    method: 'POST',
                    endpoint: '/artists',
                    description: 'Create a new artist',
                    authentication: true
                }
            ]
        },
        {
            title: 'Albums',
            description: 'Album management and metadata',
            endpoints: [
                {
                    method: 'GET',
                    endpoint: '/albums',
                    description: 'Get list of albums',
                    authentication: true
                },
                {
                    method: 'POST',
                    endpoint: '/albums',
                    description: 'Create a new album',
                    authentication: true
                }
            ]
        }
    ];

    filteredSections: ApiSection[] = [];

    constructor(
        private baseTemplateService: BaseTemplateService,
        private authService: AuthService,
    ) { }

    ngOnInit(): void {
        this.hasApiAccess = this.readApiAccess();
        this.baseTemplateService.showHideSearch(false);
        // Set the API docs URL based on environment
        this.apiDocsUrl = environment.docUrl || 'https://dev.api.rapalmanac.com/docs#';
        this.apiBaseUrl = environment.apiBaseUrl || 'https://dev.api.rapalmanac.com';
        this.filteredSections = this.apiSections;
    }

    // Fails closed. permissionsMap is granted-only with every value literally 1
    // (crud-api users.js builds it that way), so absent means not granted.
    // The try/catch is load-bearing: storeUserPermissions can persist the string
    // "undefined", which JSON.parse throws on. nav-bar.component.ts:84 reads this
    // map unguarded and empties the whole sidebar when it is null - not here.
    private readApiAccess(): boolean {
        try {
            const stored = this.authService.getUserPermissions();
            const map: Partial<PermissionsMap> | null = stored ? JSON.parse(stored) : null;
            return map?.APIKeyAccess === 1;
        } catch {
            return false;
        }
    }

    selectTab(tab: string): void {
        this.selectedTab = tab;
    }

    onSearch(): void {
        if (!this.searchTerm.trim()) {
            this.filteredSections = this.apiSections;
            return;
        }

        const searchLower = this.searchTerm.toLowerCase();
        this.filteredSections = this.apiSections
            .map(section => ({
                ...section,
                endpoints: section.endpoints.filter(endpoint =>
                    endpoint.endpoint.toLowerCase().includes(searchLower) ||
                    endpoint.description.toLowerCase().includes(searchLower) ||
                    endpoint.method.toLowerCase().includes(searchLower)
                )
            }))
            .filter(section => section.endpoints.length > 0);
    }

    getMethodClass(method: string): string {
        const methodClasses: { [key: string]: string } = {
            'GET': 'method-get',
            'POST': 'method-post',
            'PUT': 'method-put',
            'DELETE': 'method-delete',
            'PATCH': 'method-patch'
        };
        return methodClasses[method] || 'method-default';
    }

    copyToClipboard(text: string): void {
        navigator.clipboard.writeText(text).then(() => {
            // Could add a toast notification here
            console.log('Copied to clipboard');
        });
    }

    togglePdfView(): void {
        this.showPdf = !this.showPdf;
    }
}
